import { Link } from "react-router-dom";
import { FiDownload, FiFileText, FiMapPin, FiMail, FiBriefcase, FiAward, FiGlobe, FiBookOpen } from "react-icons/fi";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import Reveal from "../../components/Reveal";
import CallToAction from "../../components/CallToAction";
import profile from "../../_data/profile.json";
import resume from "../../_data/resume.json";
import { yearsOfExperience } from "../../seo/meta.mjs";

/**
 * Represents the Resume page: the full CV rendered as HTML
 * plus PDF / DOCX downloads.
 *
 * @component
 */

const Resume = () => {
  const years = yearsOfExperience(profile.careerStart);

  return (
    <>
      <main className="resume container">
        <PageHeader title="Resume" description="Uncover my expertise" />

        {/* CV header card */}
        <Reveal className="cvHeader">
          <picture>
            <source type="image/webp" srcSet="/images/khachik-harutyunyan-480.webp" />
            <img src="/images/khachik-harutyunyan.jpg" alt={profile.name} width="160" height="214" />
          </picture>
          <div className="cvHeaderInfo">
            <h2>{profile.name}</h2>
            <p className="cvTitle">{profile.title}</p>
            <ul className="cvMeta">
              <li>
                <FiBriefcase aria-hidden="true" /> {profile.currentRole}
              </li>
              <li>
                <FiMapPin aria-hidden="true" /> {profile.location}
              </li>
              <li>
                <FiMail aria-hidden="true" /> <a href={`mailto:${profile.email}`}>{profile.email}</a>
              </li>
              <li>
                <FiGlobe aria-hidden="true" /> {years}+ years · Remote-friendly
              </li>
            </ul>
            <div className="cvButtons">
              <Button
                name="Download PDF"
                href={profile.cv.pdf}
                download
                icon={<FiDownload className="arrow-icon" aria-hidden="true" />}
              />
              <Button
                name="Download DOCX"
                href={profile.cv.docx}
                download
                variant="ghost"
                icon={<FiFileText className="arrow-icon" aria-hidden="true" />}
              />
              <Button name="Open PDF" href={profile.cv.pdf} variant="ghost" />
            </div>
          </div>
        </Reveal>

        <div className="cvGrid">
          <div className="cvMain">
            <Reveal as="section" className="cvSection">
              <h2>Summary</h2>
              {resume.summary.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </Reveal>

            <section className="cvSection" aria-labelledby="cv-experience">
              <Reveal>
                <h2 id="cv-experience">Experience</h2>
              </Reveal>
              <ol className="timeline">
                {resume.experience.map((job) => (
                  <Reveal as="li" key={`${job.company}-${job.start}`}>
                    <span className="timelinePeriod">
                      {job.period} · {job.location}
                    </span>
                    <h3>
                      {job.role} · <span className="hl">{job.company}</span>
                    </h3>
                    <ul className="cvBullets">
                      {job.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                    <ul className="tagList">
                      {job.tech.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>
                    {job.project && (
                      <Link className="timelineLink" to={`/portfolio/${job.project}`}>
                        View case study →
                      </Link>
                    )}
                  </Reveal>
                ))}
              </ol>
            </section>
          </div>

          <aside className="cvAside">
            <Reveal as="section" className="cvSection">
              <h2>Skills</h2>
              {resume.skills.map((group) => (
                <div className="skillGroup" key={group.group}>
                  <h3>{group.group}</h3>
                  <ul className="tagList">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </Reveal>

            <Reveal as="section" className="cvSection">
              <h2>
                <FiBookOpen aria-hidden="true" /> Education
              </h2>
              {resume.education.map((e) => (
                <p key={e.school}>
                  <strong>{e.school}</strong>
                  <br />
                  {e.location} · {e.period}
                </p>
              ))}
            </Reveal>

            <Reveal as="section" className="cvSection">
              <h2>
                <FiAward aria-hidden="true" /> Certifications
              </h2>
              <ul className="cvBullets">
                {resume.certifications.map((c) => (
                  <li key={c.name}>
                    <strong>{c.name}</strong> — {c.issuer}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal as="section" className="cvSection">
              <h2>
                <FiGlobe aria-hidden="true" /> Languages
              </h2>
              <ul className="cvBullets">
                {profile.spokenLanguages.map((l) => (
                  <li key={l.code}>
                    <strong>{l.name}</strong> — {l.level}
                  </li>
                ))}
              </ul>
            </Reveal>
          </aside>
        </div>
      </main>
      <CallToAction title="Like what you see? Let's work together." />
    </>
  );
};

export default Resume;
